import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

type Variant = 'primary' | 'ghost' | 'gold' | 'link';
type Size = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
}

const variants: Record<Variant, string> = {
  primary: 'bg-ink text-bone hover:bg-gold hover:text-ink',
  ghost:   'bg-transparent text-ink border border-ink/15 hover:border-gold hover:text-ink',
  gold:    'bg-gold text-ink hover:bg-gold-deep',
  link:    'bg-transparent text-ink underline-offset-4 hover:text-gold-deep hover:underline px-0',
};

const sizes: Record<Size, string> = {
  sm: 'h-9 px-4 text-sm',
  md: 'h-11 px-6 text-sm',
  lg: 'h-14 px-8 text-base',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', loading, disabled, children, ...rest }, ref) => {
    return (
      <button
        ref={ref}
        disabled={disabled || loading}
        className={cn(
          'inline-flex items-center justify-center gap-2 font-body font-medium tracking-wide rounded-xs',
          'transition-all duration-200 ease-out-soft',
          'disabled:opacity-40 disabled:cursor-not-allowed',
          variants[variant],
          variant !== 'link' && sizes[size],
          className,
        )}
        {...rest}
      >
        {loading ? <span className="inline-block h-3 w-3 rounded-full border border-current border-t-transparent animate-spin" /> : null}
        {children}
      </button>
    );
  },
);
Button.displayName = 'Button';
